import classifyRtkError, {
	extractErrorMessage,
	extractTopLevelGraphqlMessage,
	toGraphqlErrorEntries,
	toRtkErrorShape
} from "~/helpers/rtkErrorClassification";

const RESPONSE_SNIPPET_MAX_LEN = 500;

export const AUTH_HEADER_KEYS = new Set(["authorization", "cookie", "set-cookie", "x-api-key"]);

/** Produces a redacted, length-capped string summary of an error response,
 * safe to attach to a Sentry event without leaking auth headers. */
export function sanitizeResponseSnippet(error: unknown): string | undefined {
	const err = toRtkErrorShape(error);
	if (!err) return undefined;

	const response = err.response;
	let source: unknown = undefined;
	if (response) {
		if (typeof response.body === "string") source = response.body;
		else if (response.errors) source = { errors: response.errors };
		else source = response;
	} else if (typeof err.data === "string") {
		source = err.data;
	} else if (err.data && typeof err.data === "object") {
		source = err.data;
	} else if (typeof err.message === "string") {
		source = err.message;
	}

	if (source === undefined) return undefined;

	let text: string;
	if (typeof source === "string") {
		text = source;
	} else {
		try {
			text = JSON.stringify(source, (key, value) => {
				if (AUTH_HEADER_KEYS.has(key.toLowerCase())) return "[REDACTED]";
				return value;
			});
		} catch {
			text = String(source);
		}
	}

	if (text.length <= RESPONSE_SNIPPET_MAX_LEN) return text;
	return `${text.slice(0, RESPONSE_SNIPPET_MAX_LEN)}…[truncated]`;
}

// HotChocolate's permission refusal: the token is valid, so renewing it cannot change the answer.
export const AUTHORIZATION_REFUSAL_CODE = "AUTH_NOT_AUTHORIZED";

const AUTHENTICATION_FAILURE_CODES = new Set(["AUTH_NOT_AUTHENTICATED", "UNAUTHENTICATED"]);

const hasUnauthorizedWording = (message: string | null | undefined): boolean =>
	!!message && (message.includes("not authorized") || message.includes("unauthorized"));

// AUTH_NOT_AUTHORIZED carries an expired token's "not authorized" wording, so the code settles the entry first.
const isAccessFailureEntry = (
	entry: { extensions?: { code?: string }; message?: string },
	permissionRefusalCounts: boolean
): boolean => {
	const code = entry.extensions?.code;
	if (code) {
		if (code === AUTHORIZATION_REFUSAL_CODE) return permissionRefusalCounts;
		if (AUTHENTICATION_FAILURE_CODES.has(code)) return true;
	}
	return hasUnauthorizedWording(entry.message);
};

function hasAccessFailure(error: unknown, permissionRefusalCounts: boolean): boolean {
	const err = toRtkErrorShape(error);
	if (!err) return false;

	if (err.response) {
		if (err.response.errors) {
			return err.response.errors.some((entry) => isAccessFailureEntry(entry, permissionRefusalCounts));
		}

		if (err.response.status === 401) {
			return true;
		}
	}

	if (typeof err.message === "string") {
		return hasUnauthorizedWording(err.message);
	}

	return false;
}

/** Checks if a GraphQL error response indicates the request's token failed, which is what routes it into the refresh path */
export function isUnauthorizedError(error: unknown): boolean {
	return hasAccessFailure(error, false);
}

export function isAccessDeniedError(error: unknown): boolean {
	return hasAccessFailure(error, true);
}

export function isQueryTimeoutError(error: unknown): boolean {
	return extractGraphqlErrorCode(error) === "QUERY_TIMEOUT";
}

export function isSearchTooBroadError(error: unknown): boolean {
	return extractGraphqlErrorCode(error) === "SEARCH_TOO_BROAD";
}

/** Extracts a human-readable error summary from a GraphQL error response */
export function extractGraphQLErrorSummary(error: unknown): string | null {
	const err = toRtkErrorShape(error);
	if (!err?.response) return null;

	if (err.response.errors?.length) {
		const codes = err.response.errors
			.map((e) => e.extensions?.code || e.message)
			.filter(Boolean)
			.join(", ");
		return codes || null;
	}
	if (err.response.status) {
		return `HTTP ${err.response.status}`;
	}
	return null;
}

/** HotChocolate leaves the top-level message a generic "Unexpected Execution Error" and puts the actionable text here. */
export function extractGraphqlExtensionsMessage(error: unknown): string | undefined {
	const errors = toGraphqlErrorEntries(error);
	if (!errors?.length) return undefined;
	for (const entry of errors) {
		const candidate = entry.extensions?.message;
		if (typeof candidate === "string" && candidate) return candidate;
	}
	return undefined;
}

/** Checks if an error is an AbortError — thrown when RTK Query cancels an
 * in-flight request (e.g. on resetApiState() or component unmount). These are
 * expected during the auth reset flow and should not be logged or sent to Sentry. */
export function isAbortError(error: unknown): boolean {
	if (error instanceof Error) {
		return error.name === "AbortError";
	}
	const err = toRtkErrorShape(error);
	return err?.name === "AbortError";
}

/** Checks if an error is a network-level failure (offline, DNS, etc.) */
export function isNetworkError(error: unknown): boolean {
	const err = toRtkErrorShape(error);
	if (!err) return false;

	if (err.status === "FETCH_ERROR" || err.status === "TIMEOUT_ERROR") return true;

	const matches = (msg: string) =>
		msg.includes("Network request failed") || msg.includes("fetch is not defined") || msg.includes("Failed to fetch");

	if (typeof err.message === "string") return matches(err.message);
	if (typeof err.error === "string") return matches(err.error);
	if (err.error && typeof err.error === "object" && typeof err.error.message === "string")
		return matches(err.error.message);

	return false;
}

/** Used as part of the Sentry fingerprint so different graphql failure modes
 * (auth vs validation vs server) group separately even on the same endpoint. */
export function extractGraphqlErrorCode(error: unknown): string | undefined {
	const errors = toGraphqlErrorEntries(error);
	if (!errors) return undefined;
	for (const entry of errors) {
		const code = entry.extensions?.code;
		if (typeof code === "string" && code) return code;
	}
	return undefined;
}

/** Best-effort extraction of a server-issued correlation/request id. Looks at
 * GraphQL extensions first (HotChocolate puts trace ids there), then any
 * top-level headers we may have surfaced. Returns undefined when absent. */
export function extractCorrelationId(error: unknown): string | undefined {
	const err = toRtkErrorShape(error);
	if (!err) return undefined;

	const errors = err.response?.errors;
	if (errors) {
		for (const entry of errors) {
			const ext = entry.extensions;
			if (!ext) continue;
			const candidate = ext.correlationId ?? ext.correlation_id ?? ext.requestId ?? ext.traceId ?? ext.trace_id;
			if (typeof candidate === "string" && candidate) return candidate;
		}
	}

	const data = err.data;
	if (data && typeof data === "object") {
		const rec: Partial<Record<"correlationId" | "requestId" | "traceId", unknown>> = data;
		const candidate = rec.correlationId ?? rec.requestId ?? rec.traceId;
		if (typeof candidate === "string" && candidate) return candidate;
	}

	return undefined;
}

const RETRYABLE_GRAPHQL_CODES = new Set<string>(["MONGO_UNAVAILABLE", "RATE_LIMITED"]);

export function extractRetryAfterSeconds(error: unknown): number | undefined {
	const value = toRtkErrorShape(error)?.response?.retryAfterSeconds;
	return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : undefined;
}

export function isRetryableError(error: unknown): boolean {
	const code = extractGraphqlErrorCode(error);
	if (code && RETRYABLE_GRAPHQL_CODES.has(code)) return true;
	const classification = classifyRtkError(error);
	return classification === "network" || classification === "timeout" || classification === "server";
}

const CANCELED_GRAPHQL_CODES = new Set<string>(["OPERATION_CANCELED"]);
// Matches abort wording, never status: baseApi relabels every AbortError as TIMEOUT_ERROR, which a real timeout also carries.
const CANCELED_MESSAGE_PATTERN = /(operation|request) was cancell?ed|abort/i;

/** True when the app itself cancelled the request — a client-side abort (tenant switch,
 * resetApiState, unmount) or a server-side HotChocolate cancellation — so grid callers can
 * fail the block silently rather than surfacing an error the user did not cause. */
export function isCanceledGridRequestError(error: unknown): boolean {
	if (isAbortError(error)) return true;

	const code = extractGraphqlErrorCode(error);
	if (code && CANCELED_GRAPHQL_CODES.has(code)) return true;

	const message = extractTopLevelGraphqlMessage(error) ?? extractErrorMessage(error);
	return CANCELED_MESSAGE_PATTERN.test(message);
}
